import { useState, useEffect, useRef } from 'react'
import useSafeState from 'hooks/useSafeState'
import { useSubscription } from '@apollo/client'
import { SignalingClient } from 'amazon-kinesis-video-streams-webrtc'
import { useMutation } from 'components/graphql'
import { listenDeviceStatus, establishChannelConnection } from '../gql'
import useCredentials from './useCredentials'
import throttle from 'lodash/throttle'
import versionCompare from 'helpers/versionCompare'

const useStreamViewer = ({ videoRef, hardwareId, clientId, appManagerVersion, onError, onMessage, onDeviceStateChange }) => {
	const [hasError, setHasError] = useState(null)
	const [hasClosed, setHasClosed] = useSafeState(null)
	const [loadingPeerConnection, setLoadingPeerConnection] = useState(true)
	const [isDataChannelReady, setIsDataChannelReady] = useState(false)

	const viewer = useRef({})
	const deviceCaller = useRef(null)
	const stillAliveTimer = useRef(null)
	const initWaitTimeout = useRef(null)
	const sentMessages = useRef({})
	const sentMessageId = useRef(1)

	const { data: credentials, loading: credentialsLoading } = useCredentials()
	const { data: statusData, loading: statusLoading } = useSubscription(listenDeviceStatus, {
		onError: setHasError,
		onSubscriptionData: ({ subscriptionData }) => {
			const streamStatus = subscriptionData?.data?.listenDeviceStatuses?.payload?.kinesisSessionStatus
			if(!streamStatus) {
				return null
			}
			logger('[DEVICE] status:', streamStatus)
			if(onDeviceStateChange) {
				onDeviceStateChange(streamStatus)
			}
			if(streamStatus === 'END_WITH_ERROR' || streamStatus === 'MEDIA_PROJECTION_FAIL' || streamStatus === 'WRONG_CONNECTION_PARAMS') {
				return setHasError({ type: 'DEVICE_ERROR', message: streamStatus })
			}
			if(streamStatus === 'END_SERVICE') {
				return setHasClosed(true)
			}
		},

		skip: typeof hardwareId === 'undefined',
		variables: {
			hardwareId: hardwareId
		}

	})
	const [mutate, { data: connectionData, loading: connectionDataLoading, called }] = useMutation(establishChannelConnection, {
		withIndicator: false,
		onError: setHasError,
		skip: typeof hardwareId === 'undefined',
		variables: {
			hardwareId: hardwareId
		}
	})

	const streamStatus = statusData?.listenDeviceStatuses?.payload?.kinesisSessionStatus
	const isDeviceReady = streamStatus === 'START_SUCCESS' || streamStatus === 'ALREADY_STARTED'
	const isDataLoading = connectionDataLoading || credentialsLoading || statusLoading

	const logger = (...args) => {
		/* eslint-disable-next-line */
		console.log(...args)
	}

	const initCallDevice = () => {
		logger('[INIT CALL] start')
		initWaitTimeout.current = setTimeout(() => {
			logger('[INIT CALL] timed out')
			setHasClosed(true)
			stopCallDevice()
		}, 60 * 1000)
		callDevice(0)
	}

	const callDevice = (delay) => {
		deviceCaller.current = setTimeout(() => {
			mutate()
			callDevice(delay ? 30 : 10) // 10 seconds for second call, 30 for subsequent
			logger('[INIT CALL] called')
		}, delay * 1000)
	}

	const stopCallDevice = () => {
		logger('[INIT CALL] stop')
		clearTimeout(deviceCaller.current)
		deviceCaller.current = null
		clearTimeout(initWaitTimeout.current)
		initWaitTimeout.current = null
	}

	const sendCommandMessage = (message) => {
		if(isDataChannelReady) {
			logger('[DEVICE] sending command: ', message)
			viewer.current.dataChannel.send(Buffer.from(JSON.stringify(message)))
		}
	}

	/* requests should be sequential there is onnly one response listener
	   throttle is required as to ensure parallel requests will not occur */
	const sendRequestOld = throttle(async (commandMsg) => {
		if(viewer.current.dataChannel.responsePromise) {
			await viewer.current.dataChannel.responsePromise
		}

		viewer.current.dataChannel.responsePromise = new Promise((resolve, reject) => {
			if(!isDataChannelReady) {
				return reject('dataChannel is not opened')
			}
			sendCommandMessage(commandMsg)
			viewer.current.dataChannel.onmessage = event => {
				logger('[DATA CHANNEL] received: ', JSON.parse(event.data))
				return resolve(JSON.parse(event.data))
			}
		})
		return viewer.current.dataChannel.responsePromise
	}, 50)

	const sendRequest = (commandMsg) => new Promise((resolve, reject) => {
		if(!isDataChannelReady) {
			return reject('dataChannel is not opened')
		}
		if(!viewer.current.dataChannel.onmessage) {
			viewer.current.dataChannel.onmessage = event => {
				const received = JSON.parse(event.data)
				logger('[DATA CHANNEL] received: ', received)
				const resolverById = sentMessages.current[received.id]
				if(resolverById) {
					resolverById(received)
					sentMessages.current[received.id] = null
				}
				if(onMessage) {
					onMessage(received)
				}
			}
		}
		sendCommandMessage({
			id: sentMessageId.current,
			...commandMsg
		})
		sentMessages.current[sentMessageId.current] = resolve
		sentMessageId.current = sentMessageId.current + 1
	})

	const sendStillAlive = () => {
		stillAliveTimer.current = setTimeout(() => {
			sendCommandMessage({ command: 'STILL_ALIVE' })
			sendStillAlive()
		}, 10 * 1000)
	}


	// catch error for logging
	useEffect(() => {
		if(hasError) {
			logger('[ERROR]', hasError)
			if(onError) {
				onError(hasError)
			}
		}
	}, [hasError])

	// catch connection closed for logging
	useEffect(() => {
		if(hasClosed) {
			logger('[END] connection closed')
		}
	}, [hasClosed])

	// stop calling init when device is ready
	useEffect(() => {
		if(isDeviceReady && deviceCaller.current) {
			stopCallDevice()
		}
	})

	// start init call, and cleanup after dismount
	useEffect(() => {
		if(!called && !isDeviceReady && hardwareId) {
			initCallDevice()
		}
		return () => {
			logger('[END] cleaning up')
			if(viewer.current.signalingClient) {
				viewer.current.signalingClient.close()
			}
			if(viewer.current.peerConnection) {
				viewer.current.peerConnection.close()
			}
			if(viewer.current.dataChannel) {
				viewer.current.dataChannel.close()
			}
			clearTimeout(deviceCaller.current)
			clearTimeout(stillAliveTimer.current)
		}
	}, [])

	useEffect(() => {
		if(isDataChannelReady) {
			sendCommandMessage({ command: 'STILL_ALIVE' })
			sendStillAlive()
		}
	}, [isDataChannelReady])

	if(!viewer.current.signalingClient && !isDataLoading && !hasError && !hasClosed && isDeviceReady) {
		logger('[SIG CLIENT] setting up')
		const { channelARN, endpointList, iceServerList } = connectionData.establishChannelConnection
		const { endpoint } = endpointList.find(i => i.protocol === 'WSS')

		viewer.current.signalingClient = new SignalingClient({
			channelARN: channelARN,
			channelEndpoint: endpoint,
			clientId,
			role: 'VIEWER',
			region: 'eu-west-1',
			credentials
		})

		const configuration = {
			iceServers: [
				...iceServerList.map(item => ({
					urls: item.uris,
					username: item.username,
					credential: item.password
				})),
				{ urls: `stun:stun.kinesisvideo.eu-west-1.amazonaws.com:443` }
			],
			iceTransportPolicy: 'all'
		}
		viewer.current.peerConnection = new RTCPeerConnection(configuration)

		const formValues = {
			useTrickleICE: true
		}


		viewer.current.dataChannel = viewer.current.peerConnection.createDataChannel(clientId)
		viewer.current.dataChannel.onopen = () => {
			logger('[DATA CHANNEL] opened')
			setIsDataChannelReady(true)
		}
		viewer.current.dataChannel.onclose = () => {
			logger('[DATA CHANNEL] closed, stoping still alive call')
			setIsDataChannelReady(false)
			setHasClosed(true)
			clearTimeout(stillAliveTimer.current)
			stillAliveTimer.current = null
		}

		viewer.current.signalingClient.on('open', async () => {
			// Create an SDP offer to send to the master
			await viewer.current.peerConnection.setLocalDescription(
				await viewer.current.peerConnection.createOffer({
					offerToReceiveAudio: false,
					offerToReceiveVideo: true
				})
			)

			// When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
			if (formValues.useTrickleICE) {
				viewer.current.signalingClient.sendSdpOffer(viewer.current.peerConnection.localDescription)
			}
		})

		viewer.current.signalingClient.on('sdpAnswer', async answer => {
			// Add the SDP answer to the peer connection
			await viewer.current.peerConnection.setRemoteDescription(answer)
		})
		viewer.current.signalingClient.on('iceCandidate', candidate => {
			// Add the ICE candidate received from the MASTER to the peer connection
			viewer.current.peerConnection.addIceCandidate(candidate)
		})
		//
		// viewer.current.signalingClient.on('close', () => {
		// 	setHasClosed(true)
		// })
		viewer.current.signalingClient.on('error', error => {
			setHasError({ type: 'SIGNALING_CLIENT', message: error })
		})
		viewer.current.peerConnection.addEventListener('iceconnectionstatechange', event => {
			if (viewer.current.peerConnection.iceConnectionState === 'failed') {
				/* possibly reconfigure the connection in some way here */
				/* then request ICE restart */
				viewer.current.peerConnection.restartIce()
			}
		})
		viewer.current.peerConnection.addEventListener('icecandidate', ({ candidate }) => {
			// Send any ICE candidates to the other peer
			if (candidate) {
				// When trickle ICE is enabled, send the ICE candidates as they are generated.
				if (formValues.useTrickleICE) {
					viewer.current.signalingClient.sendIceCandidate(candidate)
				}
			} else if (!formValues.useTrickleICE) {
				viewer.current.signalingClient.sendSdpOffer(viewer.current.peerConnection.localDescription)
			}
		})
		viewer.current.peerConnection.addEventListener('connectionstatechange', event => {
			const status = event.target.connectionState
			logger('[PEER CONNECTION] status changed: ', status)
			if(['new', 'checking', 'connecting'].includes(status)) {
				return setLoadingPeerConnection(true)
			}
			if(['disconnected', 'closed'].includes(status)) {
				return setHasClosed(true)
			}
			if(status === 'failed') {
				return setHasError({ type: 'PEER_CONNECTION' })
			}
			if(status === 'connected') {
				return setLoadingPeerConnection(false)
			}
		})
		viewer.current.peerConnection.addEventListener('track', event => {
			logger('[PEER CONNECTION] received video track')
			viewer.current.remoteStream = event.streams[0]
			videoRef.current.srcObject = viewer.current.remoteStream
		})
	}
	if(viewer?.current?.signalingClient?.readyState === 3) {
		logger('[SIG CLIENT] opening')
		viewer.current.signalingClient.open()
	}

	return {
		isLoading: (hasClosed || hasError) ? false : (isDataLoading || loadingPeerConnection),
		hasClosed,
		hasError,
		sendCommandMessage,
		sendRequest: appManagerVersion && versionCompare(appManagerVersion, '1.5.3') < 0 ? sendRequestOld : sendRequest,
		isDataChannelReady
	}
}

export default useStreamViewer
